import { useRef, useState, useCallback } from "react";
import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const NAV_LINKS = [
  { label: "Opportunity", hash: "#opportunity", dotColor: "bg-emerald-500" },
  { label: "Presence", hash: "#presence", dotColor: "bg-purple-500" },
  { label: "Portal", hash: "#portal", dotColor: "bg-sky-500" },
  { label: "Contact", hash: "#contact", dotColor: "bg-amber-500" },
];

// Milestones in viewBox 400x50
const MILESTONES = [
  { x: 7,   y: 44, label: "Aug 2025", sub: "Founded" },
  { x: 67,  y: 37, label: "Dec 2025", sub: "Angel round" },
  { x: 132, y: 26, label: "Jan 2026", sub: "XENI live" },
  { x: 200, y: 19, label: "Mar 2026", sub: "Robolab X" },
  { x: 263, y: 12, label: "Apr 2026", sub: "AU expansion" },
  { x: 332, y: 6,  label: "Q2 2026", sub: "Seed round" },
  { x: 396, y: 2,  label: "", sub: "" },
];

// ~35 candles, viewBox 400 wide. Runs of same direction, realistic.
// Lower y = higher price.
const CANDLES = [
  // Aug–Nov 2025: slow grind
  { x: 7,   o: 46, c: 44,   h: 47,   l: 43,   vol: 1.5 },
  { x: 19,  o: 44, c: 42,   h: 45,   l: 41,   vol: 1.8 },
  { x: 31,  o: 42, c: 41,   h: 43,   l: 40,   vol: 1.2 },
  { x: 43,  o: 41, c: 39.5, h: 42,   l: 39,   vol: 1.5 },
  { x: 55,  o: 39.5, c: 40.5, h: 41.5, l: 38.5, vol: 1.0 },
  // Dec 2025 — Angel round
  { x: 67,  o: 40.5, c: 37, h: 41,   l: 36,   vol: 4.5 },
  { x: 76,  o: 37, c: 33,   h: 38,   l: 32,   vol: 7.0 },
  { x: 85,  o: 33, c: 35,   h: 36,   l: 32,   vol: 3.5 },
  { x: 95,  o: 35, c: 33,   h: 36,   l: 32.5, vol: 2.5 },
  { x: 104, o: 33, c: 32,   h: 34,   l: 31,   vol: 1.5 },
  { x: 113, o: 32, c: 31,   h: 33,   l: 30.5, vol: 1.2 },
  // Jan 2026 — XENI live
  { x: 123, o: 31, c: 28,   h: 32,   l: 27,   vol: 5.0 },
  { x: 132, o: 28, c: 23,   h: 29,   l: 22,   vol: 8.5 },
  { x: 141, o: 23, c: 25.5, h: 26.5, l: 22,   vol: 4.0 },
  { x: 151, o: 25.5, c: 23, h: 26,   l: 22.5, vol: 2.5 },
  { x: 160, o: 23, c: 22,   h: 24,   l: 21,   vol: 2.0 },
  { x: 169, o: 22, c: 21,   h: 23,   l: 20.5, vol: 1.5 },
  // Consolidation
  { x: 179, o: 21, c: 22,   h: 22.5, l: 20,   vol: 1.8 },
  { x: 188, o: 22, c: 21,   h: 23,   l: 20.5, vol: 1.2 },
  // Mar 2026 — Robolab X
  { x: 197, o: 21, c: 18,   h: 22,   l: 17,   vol: 5.5 },
  { x: 207, o: 18, c: 15.5, h: 19,   l: 15,   vol: 6.5 },
  { x: 216, o: 15.5, c: 17.5, h: 18,  l: 14.5, vol: 3.0 },
  { x: 225, o: 17.5, c: 15, h: 18,   l: 14,   vol: 3.5 },
  { x: 235, o: 15, c: 14,   h: 16,   l: 13.5, vol: 2.0 },
  { x: 244, o: 14, c: 13,   h: 15,   l: 12.5, vol: 1.8 },
  // Apr 2026 — AU expansion
  { x: 253, o: 13, c: 10,   h: 14,   l: 9,    vol: 7.5 },
  { x: 263, o: 10, c: 7.5,  h: 11,   l: 7,    vol: 8.0 },
  { x: 272, o: 7.5, c: 10,  h: 10.5, l: 7,    vol: 4.5 },
  { x: 281, o: 10, c: 8.5,  h: 10.5, l: 8,    vol: 2.5 },
  { x: 291, o: 8.5, c: 8,   h: 9.5,  l: 7.5,  vol: 1.5 },
  // Consolidation
  { x: 300, o: 8, c: 8.5,   h: 9.5,  l: 7,    vol: 1.0 },
  { x: 309, o: 8.5, c: 7.5, h: 9,    l: 7,    vol: 1.2 },
  { x: 319, o: 7.5, c: 7,   h: 8.5,  l: 6.5,  vol: 1.0 },
  // Q2 2026 — Seed round
  { x: 328, o: 7, c: 5.5,   h: 7.5,  l: 5,    vol: 5.0 },
  { x: 337, o: 5.5, c: 4,   h: 6,    l: 3.5,  vol: 6.5 },
  { x: 347, o: 4, c: 2.5,   h: 4.5,  l: 2,    vol: 8.5 },
  { x: 356, o: 2.5, c: 4.5, h: 5,    l: 2,    vol: 5.5 },
  { x: 365, o: 4.5, c: 3,   h: 5,    l: 2.5,  vol: 4.0 },
  { x: 375, o: 3, c: 2.5,   h: 4,    l: 2,    vol: 3.5 },
  { x: 385, o: 2.5, c: 3.5, h: 4,    l: 2,    vol: 2.0 },
  { x: 396, o: 3.5, c: 2,   h: 4,    l: 1.5,  vol: 4.5 },
];

function pricePath(): string {
  return CANDLES.map((c, i) => `${i === 0 ? "M" : "L"}${c.x},${c.c}`).join(" ");
}

function areaPath(linePath: string): string {
  const last = CANDLES[CANDLES.length - 1]!;
  const first = CANDLES[0]!;
  return `${linePath} L${last.x},49 L${first.x},49 Z`;
}

// Find nearest candle to mouse x
function getNearestCandle(x: number) {
  let best = CANDLES[0]!;
  let bestDist = Infinity;
  for (const c of CANDLES) {
    const d = Math.abs(c.x - x);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

// Find closest milestone for context
function getNearestMilestone(x: number): { label: string; sub: string } | null {
  let closest = MILESTONES[0]!;
  let minDist = Infinity;
  for (const m of MILESTONES) {
    if (!m.label) continue;
    const d = Math.abs(m.x - x);
    if (d < minDist) { minDist = d; closest = m; }
  }
  if (minDist < 30) return { label: closest.label, sub: closest.sub };
  return null;
}

type CrosshairState = {
  candle: typeof CANDLES[0];
  mouseY: number;
  milestone: { label: string; sub: string } | null;
};

export function IRHero() {
  const prefersReducedMotion = useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  const [crosshair, setCrosshair] = useState<CrosshairState | null>(null);

  const line = pricePath();
  const area = areaPath(line);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const inv = ctm.inverse();
    const vx = inv.a * e.clientX + inv.c * e.clientY + inv.e;
    const vy = inv.b * e.clientX + inv.d * e.clientY + inv.f;
    const candle = getNearestCandle(vx);
    const milestone = getNearestMilestone(candle.x);
    setCrosshair({ candle, mouseY: vy, milestone });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setCrosshair(null);
  }, []);

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            Investors
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            Building the AI agent platform for regulated industries. If you're interested in what we're doing, here's how to learn more.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${link.dotColor}`} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* K-line chart strip */}
      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden mt-2 md:mt-4 hero-strip-mask">
          <svg
            ref={svgRef}
            viewBox="0 0 400 50"
            className="w-full h-[160px] md:h-[220px]"
            preserveAspectRatio="xMidYMid meet"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <defs>
              <linearGradient id="irAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(16,185,129)" stopOpacity="0.12" />
                <stop offset="100%" stopColor="rgb(16,185,129)" stopOpacity="0" />
              </linearGradient>
              <filter id="irGlow">
                <feGaussianBlur stdDeviation="0.3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Background grid — horizontal */}
            {[5, 10, 15, 20, 25, 30, 35, 40, 45].map((y) => (
              <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="var(--color-fg)" strokeWidth="0.06" opacity={0.04} />
            ))}
            {/* Background grid — vertical */}
            {Array.from({ length: 40 }, (_, i) => i * 10 + 5).map((x) => (
              <line key={x} x1={x} y1="0" x2={x} y2="50" stroke="var(--color-fg)" strokeWidth="0.06" opacity={0.03} />
            ))}

            {/* Milestone vertical markers */}
            {MILESTONES.filter((m) => m.label).map((m) => (
              <line key={m.x} x1={m.x} y1={0} x2={m.x} y2="49" stroke="var(--color-fg)" strokeWidth="0.08" opacity={0.08} strokeDasharray="0.4 0.8" />
            ))}

            {/* Candlestick bars */}
            {CANDLES.map((c) => {
              const bullish = c.c < c.o;
              const color = bullish ? "rgb(16,185,129)" : "rgb(239,68,68)";
              const bodyTop = Math.min(c.o, c.c);
              const bodyH = Math.max(Math.abs(c.o - c.c), 0.3);
              const isActive = crosshair?.candle.x === c.x;
              return (
                <g key={c.x}>
                  <line x1={c.x} y1={c.l} x2={c.x} y2={c.h} stroke={color} strokeWidth="0.25" opacity={isActive ? 0.7 : 0.4} />
                  <rect x={c.x - 2.2} y={bodyTop} width="4.4" height={bodyH} fill={color} opacity={isActive ? 0.6 : 0.35} rx="0.15" />
                </g>
              );
            })}

            {/* Volume bars at bottom */}
            {CANDLES.map((c) => {
              const bullish = c.c < c.o;
              return (
                <rect
                  key={`v${c.x}`}
                  x={c.x - 1.8}
                  y={49 - c.vol}
                  width="3.6"
                  height={c.vol}
                  fill={bullish ? "rgb(16,185,129)" : "rgb(239,68,68)"}
                  opacity={0.15}
                  rx="0.15"
                />
              );
            })}

            {/* Area fill */}
            <path d={area} fill="url(#irAreaGrad)" />

            {/* Closing price line */}
            <path
              d={line}
              fill="none"
              stroke="rgb(16,185,129)"
              strokeWidth="0.25"
              opacity={0.5}
              filter="url(#irGlow)"
              strokeLinejoin="round"
            />

            {/* Traveling pulse */}
            {!prefersReducedMotion && (
              <circle r="0.5" fill="rgb(16,185,129)" opacity={0}>
                <animateMotion dur="8s" repeatCount="indefinite" path={line} />
                <animate attributeName="opacity" values="0;0.8;0.8;0" dur="8s" repeatCount="indefinite" />
                <animate attributeName="r" values="0.4;0.8;0.4" dur="8s" repeatCount="indefinite" />
              </circle>
            )}

            {/* Milestone dots + labels */}
            {MILESTONES.filter((m) => m.label).map((m, i) => (
              <g key={i}>
                <circle cx={m.x} cy={m.y} r="1.2" fill="none" stroke="rgb(16,185,129)" strokeWidth="0.15" opacity={0.15} />
                <circle cx={m.x} cy={m.y} r="0.5" fill="rgb(16,185,129)" opacity={0.7} />
                <text x={m.x} y={m.y - 2.5} fill="var(--color-fg)" opacity={0.35} fontSize="1.6" fontFamily="var(--font-mono)" textAnchor="middle">
                  {m.label}
                </text>
                <text x={m.x} y={m.y - 1} fill="var(--color-fg)" opacity={0.2} fontSize="1.3" fontFamily="var(--font-mono)" textAnchor="middle">
                  {m.sub}
                </text>
              </g>
            ))}

            {/* Crosshair — snaps to nearest candle */}
            {crosshair && (() => {
              const c = crosshair.candle;
              const bullish = c.c < c.o;
              const color = bullish ? "rgb(16,185,129)" : "rgb(239,68,68)";
              const tipX = c.x > 300 ? c.x - 32 : c.x + 3;
              return (
                <g>
                  {/* Vertical line — snapped to candle */}
                  <line x1={c.x} y1="0" x2={c.x} y2="50" stroke="var(--color-fg)" strokeWidth="0.1" opacity={0.25} strokeDasharray="0.4 0.6" />
                  {/* Horizontal line — snapped to candle close price */}
                  <line x1="0" y1={c.c} x2="400" y2={c.c} stroke="var(--color-fg)" strokeWidth="0.1" opacity={0.25} strokeDasharray="0.4 0.6" />
                  {/* Dot on candle close price */}
                  <circle cx={c.x} cy={c.c} r="0.8" fill={color} opacity={0.9} />
                  <circle cx={c.x} cy={c.c} r="1.8" fill="none" stroke={color} strokeWidth="0.15" opacity={0.4} />

                  {/* OHLC tooltip */}
                  <rect
                    x={tipX}
                    y={Math.max(1, c.c - 7)}
                    width="29"
                    height="7"
                    fill="var(--color-surface)"
                    stroke="var(--color-fg)"
                    strokeWidth="0.08"
                    opacity={0.92}
                    rx="0.3"
                  />
                  {/* Milestone label if nearby */}
                  {crosshair.milestone && (
                    <text x={tipX + 1} y={Math.max(1, c.c - 7) + 1.5} fill={color} fontSize="1.3" fontFamily="var(--font-mono)" fontWeight="bold">
                      {crosshair.milestone.sub}
                    </text>
                  )}
                  {/* OHLC values */}
                  <text x={tipX + 1} y={Math.max(1, c.c - 7) + (crosshair.milestone ? 3.2 : 2)} fill="var(--color-fg)" opacity={0.6} fontSize="1.1" fontFamily="var(--font-mono)">
                    O {c.o.toFixed(1)}  H {c.h.toFixed(1)}  L {c.l.toFixed(1)}  C {c.c.toFixed(1)}
                  </text>
                  {/* Volume */}
                  <text x={tipX + 1} y={Math.max(1, c.c - 7) + (crosshair.milestone ? 4.8 : 3.6)} fill="var(--color-fg)" opacity={0.4} fontSize="1" fontFamily="var(--font-mono)">
                    Vol {c.vol.toFixed(1)}
                  </text>
                  {/* Price label on right edge */}
                  <rect x="393" y={c.c - 1} width="7" height="2" fill={color} opacity={0.8} rx="0.2" />
                  <text x="396.5" y={c.c + 0.4} fill="var(--color-bg)" fontSize="1.1" fontFamily="var(--font-mono)" textAnchor="middle">
                    {c.c.toFixed(0)}
                  </text>
                </g>
              );
            })()}
          </svg>
        </div>
      </Reveal>
    </section>
  );
}
